import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, DollarSign } from 'lucide-react';
import { packagesAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const ManagePackages = () => {
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price_usd: '',
    price_bdt: '',
    credits: '',
    duration_days: '30',
    features: '',
    is_active: true,
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await packagesAPI.getAll(false);
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        price_usd: pkg.price_usd.toString(),
        price_bdt: pkg.price_bdt.toString(),
        credits: pkg.credits.toString(),
        duration_days: (pkg.duration_days || 30).toString(),
        features: pkg.features?.join('\n') || '',
        is_active: pkg.is_active,
      });
    } else {
      setEditingPackage(null);
      setFormData({
        name: '',
        price_usd: '',
        price_bdt: '',
        credits: '',
        duration_days: '30',
        features: '',
        is_active: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const featuresArray = formData.features.split('\n').map((f) => f.trim()).filter(Boolean);
      
      const data = {
        name: formData.name,
        price_usd: parseFloat(formData.price_usd),
        price_bdt: parseFloat(formData.price_bdt),
        credits: parseInt(formData.credits),
        duration_days: parseInt(formData.duration_days) || 30,
        features: featuresArray,
        is_active: formData.is_active,
      };

      if (editingPackage) {
        await packagesAPI.update(editingPackage.id, data);
      } else {
        await packagesAPI.create(data);
      }
      
      setIsModalOpen(false);
      fetchPackages();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save package');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    
    try {
      await packagesAPI.delete(id);
      fetchPackages();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete package');
    }
  };

  const toggleActive = async (id) => {
    try {
      await packagesAPI.toggleActive(id);
      fetchPackages();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to toggle active state');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Manage Packages</h1>
          <p className="text-gray-400">Configure pricing packages and credits</p>
        </div>
        <Button onClick={() => openModal()} className="bg-purple-600 hover:bg-purple-700">
          <Plus size={18} className="mr-2" />
          Add Package
        </Button>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        /* Packages Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-[#1a1a2e] rounded-xl p-6 border transition-all ${
                pkg.is_active ? 'border-gray-800 hover:border-purple-500/50' : 'border-red-800/50 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold text-lg">{pkg.name}</h3>
                <Switch
                  checked={pkg.is_active}
                  onCheckedChange={() => toggleActive(pkg.id)}
                />
              </div>

              <div className="mb-4">
                <p className="text-purple-400 text-3xl font-bold">${pkg.price_usd.toFixed(2)}</p>
                <p className="text-orange-400 text-sm">৳{pkg.price_bdt.toFixed(2)}</p>
              </div>

              <div className="mb-4">
                <p className="text-white text-2xl font-bold">{pkg.credits}</p>
                <p className="text-gray-400 text-sm">credits for {pkg.duration_days || 30} days</p>
              </div>

              <div className="space-y-2 mb-4">
                {pkg.features?.map((feature, index) => (
                  <p key={index} className="text-gray-400 text-sm flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                    {feature}
                  </p>
                ))}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => openModal(pkg)}
                  variant="outline"
                  className="flex-1 border-gray-700 text-gray-400 hover:text-white"
                >
                  <Edit2 size={16} className="mr-2" />
                  Edit
                </Button>
                <Button
                  onClick={() => handleDelete(pkg.id)}
                  variant="outline"
                  className="border-red-800 text-red-400 hover:bg-red-900/30"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-gray-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <DollarSign size={20} />
              {editingPackage ? 'Edit Package' : 'Add New Package'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-400">Package Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#12121a] border-gray-700 text-white"
                placeholder="e.g., Starter, Pro, Enterprise"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Price (USD)</Label>
                <Input
                  type="number"
                  value={formData.price_usd}
                  onChange={(e) => setFormData({ ...formData, price_usd: e.target.value })}
                  className="bg-[#12121a] border-gray-700 text-white"
                  placeholder="9.00"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Price (BDT)</Label>
                <Input
                  type="number"
                  value={formData.price_bdt}
                  onChange={(e) => setFormData({ ...formData, price_bdt: e.target.value })}
                  className="bg-[#12121a] border-gray-700 text-white"
                  placeholder="1080.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Credits</Label>
                <Input
                  type="number"
                  value={formData.credits}
                  onChange={(e) => setFormData({ ...formData, credits: e.target.value })}
                  className="bg-[#12121a] border-gray-700 text-white"
                  placeholder="1000"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Duration (days)</Label>
                <Input
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                  className="bg-[#12121a] border-gray-700 text-white"
                  placeholder="30"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400">Features (one per line)</Label>
              <textarea
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                className="w-full min-h-[100px] bg-[#12121a] border border-gray-700 rounded-md p-3 text-white placeholder:text-gray-500"
                placeholder="500 Credits&#10;Basic Prompts&#10;Email Support"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-gray-400">Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)} className="border-gray-700 text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
              <Save size={16} className="mr-2" />
              Save Package
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagePackages;
